import {
  ActionsMenu,
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BenefitsFilter,
  EmployeesGroupsFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  OrgStructureFilter,
  SelectFilter,
} from 'components/filters';
import { ROUTES } from 'config';
import { useModalContext } from 'store/modal';
import { DownloadXlsAction } from 'components/actions';
import { useMe } from 'state/Administrators';
import {
  EmployeeGroup,
  employeeGroupsFiltersSchema,
  useEmployeeGroups,
  useExportToXls,
} from 'store/employee-groups';
import { useSearchParams } from 'utils/navigation';
import {
  CreateEmployeeGroupModal,
  EmployeeGroupActions,
} from 'containers/employees-groups';
import { benefitStatuses } from 'store/benefits';
import { employeeStatuses } from 'store/employees';

export const EmployeeGroupsList: FC = () => {
  const { t } = useTranslation();
  const { canEditGroups, hasEditPermission, hasViewPermission } = useMe();
  const { openModal } = useModalContext();

  const { searchParams: filters, navigate } = useSearchParams(
    employeeGroupsFiltersSchema,
  );

  const { data: employeeGroups, isLoading } = useEmployeeGroups(filters);

  const { mutate: exportToXls, isLoading: isExporting } = useExportToXls();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}
          >
            <ActionsMenu
              type="spread"
              items={[
                {
                  label: t('buttons.add_employee_group'),
                  disabled: !canEditGroups || !hasEditPermission(['groups']),
                  color: 'primary',
                  onClick: () => {
                    openModal({
                      component: CreateEmployeeGroupModal,
                    });
                  },
                },
              ]}
            />
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <ListTotal total={employeeGroups?.total} />

            <Stack direction="row" alignItems="center" gap={1}>
              <Filters
                value={filters}
                initialValues={employeeGroupsFiltersSchema.parse({})}
                onChange={(v) => navigate({ params: v })}
                keysToExtractFromTotalCalculation={['name']}
              >
                {({ value, initialValues, setFilter }) => {
                  return (
                    <>
                      <FilterButton
                        label={t('entity_type.employeeGroup')}
                        value={value.employeeGroups}
                        initialValue={initialValues.employeeGroups}
                        onChange={(v) => setFilter('employeeGroups', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <EmployeesGroupsFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>

                      {hasViewPermission(['benefits']) ? (
                        <>
                          <FilterButton
                            label={t('common.plan_name')}
                            value={value.benefits}
                            initialValue={initialValues.benefits}
                            onChange={(v) => setFilter('benefits', v)}
                          >
                            {({ value, applyFilter, clearFilter, close }) => (
                              <BenefitsFilter
                                value={{ value }}
                                onApplyFilter={({ value }) =>
                                  applyFilter(value)
                                }
                                onClearFilter={clearFilter}
                                onCancel={close}
                              />
                            )}
                          </FilterButton>

                          <FilterButton
                            label={t('wallet.employeeWalletBenefitStatus')}
                            value={value.benefitStatus}
                            initialValue={initialValues.benefitStatus}
                            onChange={(v) => setFilter('benefitStatus', v)}
                          >
                            {({ value, applyFilter, clearFilter, close }) => (
                              <SelectFilter
                                value={{ value }}
                                onApplyFilter={({ value }) =>
                                  applyFilter(value)
                                }
                                onClearFilter={clearFilter}
                                onCancel={close}
                                multiple
                                options={benefitStatuses.map((status) => ({
                                  key: status,
                                  label: t(`status.${status}`),
                                }))}
                                searchable={false}
                              />
                            )}
                          </FilterButton>
                        </>
                      ) : null}

                      <FilterButton
                        label={t('employees.employee_status.label')}
                        value={value.employeeStatus}
                        initialValue={initialValues.employeeStatus}
                        onChange={(v) => setFilter('employeeStatus', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            multiple
                            options={employeeStatuses.map((status) => ({
                              key: status,
                              label: t(`status.${status}`),
                            }))}
                            searchable={false}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('home.organisationStructure_title')}
                        value={value.orgStructure}
                        initialValue={initialValues.orgStructure}
                        onChange={(v) => setFilter('orgStructure', v)}
                        contentMaxWidth={420}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <OrgStructureFilter
                            value={value}
                            onApplyFilter={applyFilter}
                            onClearFilter={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>

                      <OpenSearchFilter
                        value={filters.name}
                        onApplyFilter={(v) => setFilter('name', v)}
                        placeholder={t('common.search')}
                      />
                    </>
                  );
                }}
              </Filters>

              <Divider orientation="vertical" variant="middle" flexItem />

              <DownloadXlsAction
                listLength={employeeGroups?.total ?? 0}
                exportToExcel={() => exportToXls({ filters })}
                loading={isExporting}
              />
            </Stack>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={employeeGroups?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'name',
            header: t('entity_type.employeeGroup'),
            cell: ({ row: { original: group } }) => {
              return hasViewPermission(['benefits', 'employees']) ? (
                <Link
                  label={group.name}
                  to={ROUTES.employeeGroups.details.replace(':id', group.id)}
                />
              ) : (
                group.name
              );
            },
          },
          {
            id: 'employeeCount',
            header: t('exportFileName.employees'),
            cell: ({ row: { original: group } }) => {
              return group.employeeCount;
            },
            meta: { align: 'right', sort: true },
          },
          ...(hasViewPermission(['benefits'])
            ? [
                {
                  id: 'benefitCount',
                  header: t('menu.benefits'),
                  cell: ({
                    row: { original: group },
                  }: {
                    row: { original: EmployeeGroup };
                  }) => {
                    return group.benefitCount;
                  },
                  meta: { align: 'right' as const, sort: true },
                },
                {
                  id: 'benefits',
                  header: t('menu.benefits'),
                  cell: ({
                    row: { original: group },
                  }: {
                    row: { original: EmployeeGroup };
                  }) => {
                    return (
                      <Stack>
                        {group.benefits.map((benefit) => (
                          <Link
                            label={benefit.name}
                            to={ROUTES.benefits.details.replace(
                              ':id',
                              benefit.id,
                            )}
                            key={benefit.id}
                          />
                        ))}
                      </Stack>
                    );
                  },
                },
              ]
            : []),
          {
            id: 'actions',
            cell: ({ row: { original: group } }) => {
              return canEditGroups && !group.isAllEmployeesGroup ? (
                <EmployeeGroupActions employeeGroup={group} type="compact" />
              ) : null;
            },
          },
        ]}
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        rows={{
          inactive: ({ original: employee }) => employee.employeeCount === 0,
        }}
      />
    </TableWrapper>
  );
};
